import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getImageUrl } from '../../utils/imageUtils';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Grid,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Role-based field restrictions
const getRestrictedFields = (userRole: string) => {
  if (userRole === 'shipment_manager') {
    return ['unit_price', 'cost_price', 'tax_rate', 'stock_quantity', 'stock_single'];
  }
  return [];
};

// Role-based default values for restricted fields
const getDefaultValuesForRestrictedFields = (userRole: string) => {
  if (userRole === 'shipment_manager') {
    return {
      unit_price: 0,
      cost_price: 0,
      tax_rate: 0,
      stock_quantity: 0,
      stock_single: 0
    };
  }
  return {};
};

// Check if field should be hidden for current user
const isFieldRestricted = (fieldName: string, userRole: string) => {
  return getRestrictedFields(userRole).includes(fieldName);
};

// Mock data for categories
const mockCategories = [
  { id: 1, name: 'Electronics' },
  { id: 2, name: 'Office Supplies' },
  { id: 3, name: 'Furniture' },
];

// Default fallback unit types (in case API fails)
const defaultUnitTypes = [
  'Piece',
  'Pack', 
  'Box',
  'Bundle',
  'Bottle',
];

// Mock data for tax options
const mockTaxOptions = [
  { id: 1, name: 'No Tax', rate: 0, isDefault: true },
  { id: 2, name: 'Standard Rate', rate: 20, isDefault: false },
];

// Helper function to find the correct tax option ID based on the tax rate
const findTaxOptionId = (taxRate: number | string | null | undefined): number => {
  if (taxRate === null || taxRate === undefined) return 1; // Default to 'No Tax'
  
  // Convert to number for comparison
  const rate = typeof taxRate === 'string' ? parseFloat(taxRate) : taxRate;
  
  // Try to find exact match
  const exactMatch = mockTaxOptions.find(t => t.rate === rate);
  if (exactMatch) return exactMatch.id;
  
  // If no exact match, find the closest match
  if (rate > 0) {
    // If tax rate is greater than 0, default to Standard Rate
    return 2; // Standard Rate ID
  }
  
  // Default to No Tax
  return 1;
};

// Mock product data
const mockProducts = [
  {
    id: 1,
    sku: 'ELEC-001',
    name: 'Laptop',
    categoryId: 1,
    image: 'https://via.placeholder.com/150',
    unit_size: '2.5kg',
    qtyInBox: 1,
    unitTypeId: 1,
    unitPrice: 999.99,
    taxOptionId: 2,
    stockCount: 50,
  },
  {
    id: 2,
    sku: 'ELEC-002',
    name: 'Smartphone',
    categoryId: 1,
    image: 'https://via.placeholder.com/150',
    unit_size: '300g',
    qtyInBox: 1,
    unitTypeId: 1,
    unitPrice: 699.99,
    taxOptionId: 2,
    stockCount: 100,
  },
];

interface FormData {
  sku: string;
  name: string;
  categoryId: number;
  image: string;
  unit_size: string;
  qtyInBox: number;
  unitTypeId: string;
  costPrice: number;
  unitPrice: number;
  taxOptionId: number;
  stockCount: number;
  stockSingle: number;
}

interface FormErrors {
  sku?: string;
  name?: string;
  categoryId?: string;
  unit_size?: string;
  qtyInBox?: string;
  unitTypeId?: string;
  costPrice?: string;
  unitPrice?: string;
  taxOptionId?: string;
  stockCount?: string;
  stockSingle?: string;
}

const ProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const isEditMode = Boolean(id);
  const userRole = user?.role || '';
  const isShipmentManager = userRole === 'shipment_manager';

  const [formData, setFormData] = useState<FormData>({
    sku: '',
    name: '',
    categoryId: 0,
    image: '',
    unit_size: '',
    qtyInBox: 1,
    unitTypeId: '',
    costPrice: 0,
    unitPrice: 0,
    taxOptionId: 0,
    stockCount: 0,
    stockSingle: 0,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [unitTypes, setUnitTypes] = useState<string[]>([]);
  const [taxOptions, setTaxOptions] = useState<any[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isTypingNewUnit, setIsTypingNewUnit] = useState(false);
  const [customUnitType, setCustomUnitType] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch categories from the backend API
        const categoriesResponse = await api.get('/categories');
        if (categoriesResponse.data && categoriesResponse.data.success) {
          setCategories(categoriesResponse.data.data);
        } else {
          // Fall back to mock data if API call fails
          console.warn('No category data received from API, using mock data');
          setCategories(mockCategories);
        }
        
        // Fetch unit types from the backend API
        let fetchedUnitTypes = defaultUnitTypes;
        try {
          const unitTypesResponse = await api.get('/products/unit-types');
          if (unitTypesResponse.data && unitTypesResponse.data.success) {
            fetchedUnitTypes = unitTypesResponse.data.data;
            setUnitTypes(fetchedUnitTypes);
          } else {
            // Fall back to default unit types if API call fails
            console.warn('No unit types data received from API, using default unit types');
            setUnitTypes(defaultUnitTypes);
          }
        } catch (unitTypesError) {
          console.warn('Failed to fetch unit types, using default unit types:', unitTypesError);
          setUnitTypes(defaultUnitTypes);
        }
        
        // Still using mock data for tax options
        setTaxOptions(mockTaxOptions);

        // Load product data after we have unit types (for edit mode)
        if (isEditMode && id) {
          try {
            const response = await api.get(`/products/${id}`);
            
            if (response.data && response.data.success) {
              const productData = response.data.data;
              
              // Transform the API data to match our form structure
              const productUnitType = productData.unit_type || '';
              
              // Check if the product's unit type exists in our fetched unit types
              // If not, we'll treat it as a custom unit type
              if (productUnitType && !fetchedUnitTypes.includes(productUnitType)) {
                setIsTypingNewUnit(true);
                setCustomUnitType(productUnitType);
              }
              
              setFormData({
                sku: productData.sku,
                name: productData.name,
                categoryId: productData.category_id || 0,
                image: productData.image_url || '',
                unit_size: productData.unit_size || '',
                qtyInBox: productData.qty_in_box || 1,
                unitTypeId: fetchedUnitTypes.includes(productUnitType) ? productUnitType : '',
                costPrice: productData.cost_price || 0,
                unitPrice: productData.unit_price || 0,
                taxOptionId: findTaxOptionId(productData.tax_rate),
                stockCount: productData.stock_quantity || 0,
                stockSingle: productData.stock_single || 0,
              });
              // Use getImageUrl to properly format the image URL
              setImagePreview(productData.image_url ? getImageUrl(productData.image_url) : null);
            } else {
              // If API call succeeds but returns no data, show error
              setSubmitError('Product not found');
              console.error('Product not found:', response.data);
            }
          } catch (error) {
            console.error('Error loading product data:', error);
            setSubmitError('Failed to load product data');
          }
        } else {
          // Set default values for new product
          setFormData(prevData => ({
            ...prevData,
            taxOptionId: mockTaxOptions.find(t => t.isDefault)?.id || 1,
          }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setSubmitError('Failed to load product data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && isAuthenticated) {
      fetchData();
    }
  }, [authLoading, isAuthenticated, id, isEditMode]);

  // Cleanup image preview URLs when component unmounts
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.sku.trim()) {
      newErrors.sku = 'SKU is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    if (!formData.unit_size.trim()) {
      newErrors.unit_size = 'Unit size is required';
    }

    if (formData.qtyInBox < 1) {
      newErrors.qtyInBox = 'Quantity in a box must be at least 1';
    }

    if (isTypingNewUnit) {
      if (!customUnitType.trim()) {
        newErrors.unitTypeId = 'Unit type is required';
      }
    } else {
      if (!formData.unitTypeId.trim()) {
        newErrors.unitTypeId = 'Unit type is required';
      }
    }

    // Skip validation for restricted fields if user is shipment manager
    if (!isFieldRestricted('unit_price', userRole)) {
      if (formData.unitPrice <= 0) {
        newErrors.unitPrice = 'Unit price must be greater than 0';
      }
    }

    if (!isFieldRestricted('stock_quantity', userRole)) {
      if (formData.stockCount < 0) {
        newErrors.stockCount = 'Stock count must be a positive number';
      }
    }

    if (!isFieldRestricted('stock_single', userRole)) {
      if (formData.stockSingle < 0) {
        newErrors.stockSingle = 'Stock (Single) must be a positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle both input changes and select changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | any) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Special handler for unit type selection
  const handleUnitTypeChange = (e: any) => {
    const value = e.target.value;
    if (value === '___TYPE_NEW_UNIT___') {
      setIsTypingNewUnit(true);
      setCustomUnitType('');
      setFormData({
        ...formData,
        unitTypeId: '',
      });
    } else {
      setIsTypingNewUnit(false);
      setCustomUnitType('');
      setFormData({
        ...formData,
        unitTypeId: value,
      });
    }
  };

  // Handler for custom unit type input
  const handleCustomUnitTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomUnitType(e.target.value);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Clean up previous preview URL if it exists
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      
      // Create a local URL for preview
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
      
      try {
        // Create a FormData object to send the file to the server
        const formData = new FormData();
        formData.append('image', file);
        
        // Upload the image to the server
        const response = await api.post('/upload/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data && response.data.success) {
          // Clean up the temporary preview URL
          URL.revokeObjectURL(imageUrl);
          
          // Set the image path from the server response
          // Store only the relative path, not the full URL
          setFormData(prevData => ({
            ...prevData,
            image: response.data.data.path,
          }));
          
          // Update preview to use the server image
          setImagePreview(getImageUrl(response.data.data.path));
        } else {
          console.error('Error uploading image:', response.data);
          // Still use the local preview but log the error
          setFormData(prevData => ({
            ...prevData,
            image: imageUrl,
          }));
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        // Fall back to local preview on error
        setFormData(prevData => ({
          ...prevData,
          image: imageUrl,
        }));
      }
    }
  };

  const handleRemoveImage = () => {
    // Clean up preview URL if it's a blob URL
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }

    // Clear the image preview and form data
    setImagePreview(null);
    setFormData(prevData => ({
      ...prevData,
      image: '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Transform form data to match the API structure
      const selectedTaxOption = taxOptions.find(t => t.id === formData.taxOptionId);
      
      // Base product data with user-entered fields
      let productData: any = {
        sku: formData.sku,
        name: formData.name,
        description: '', // Add a description field to the form if needed
        category_id: formData.categoryId,
        unit_size: formData.unit_size,
        qty_in_box: formData.qtyInBox,
        unit_type: isTypingNewUnit ? customUnitType : formData.unitTypeId,
        image_url: formData.image,
        is_active: true
      };

      // Add fields based on role permissions
      if (isShipmentManager) {
        if (isEditMode) {
          // For shipment manager editing, preserve existing restricted field values
          // Only include non-restricted fields in the update
          productData = {
            ...productData,
            reorder_level: 10
          };
          // Note: Restricted fields (cost_price, unit_price, tax_rate, stock_quantity, stock_single) 
          // are intentionally omitted to preserve existing values
        } else {
          // For shipment manager creating new product, use defaults for restricted fields
          const defaults = getDefaultValuesForRestrictedFields(userRole);
          productData = {
            ...productData,
            ...defaults,
            reorder_level: 10
          };
        }
      } else {
        // For other roles, include all fields from form
        productData = {
          ...productData,
          cost_price: formData.costPrice,
          unit_price: formData.unitPrice,
          tax_rate: selectedTaxOption?.rate || 0,
          stock_quantity: formData.stockCount,
          stock_single: formData.stockSingle,
          reorder_level: 10
        };
      }

      let response;
      if (isEditMode && id) {
        // Update existing product - use regular endpoint for all roles
        response = await api.put(`/products/${id}`, productData);
        console.log('Product updated:', response.data);
      } else {
        // Create new product - use different endpoints based on role
        if (isShipmentManager) {
          response = await api.post('/products/limited', productData);
          console.log('Product created (limited):', response.data);
        } else {
          response = await api.post('/products', productData);
          console.log('Product created:', response.data);
        }
      }

      if (response.data && response.data.success) {
        // Navigate back to product list on success
        navigate('/products');
      } else {
        setSubmitError('Failed to save product. Please try again.');
      }
    } catch (error: any) {
      console.error('Error saving product:', error);
      
      let errorMessage = 'Failed to save product. Please try again.';
      
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Format validation errors for display
        const validationErrors = error.response.data.errors
          .map((err: any) => `${err.field}: ${err.message}`)
          .join(', ');
        errorMessage = `Validation failed: ${validationErrors}`;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/products')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4">
          {isEditMode ? 'Edit Product' : 'Add Product'}
        </Typography>
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3 }}>
            {/* Left column */}
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Product Image
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    border: '1px dashed #ccc',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  {imagePreview ? (
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Product preview"
                      onError={(e) => {
                        // If image fails to load, replace with placeholder
                        (e.target as HTMLImageElement).src = getImageUrl(null);
                      }}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <>
                      <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Click to upload image
                      </Typography>
                    </>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
                {imagePreview && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    fullWidth
                    onClick={handleRemoveImage}
                    sx={{ mt: 1 }}
                  >
                    Remove Image
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {!isFieldRestricted('stock_quantity', userRole) && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Inventory
                  </Typography>
                  <TextField
                    fullWidth
                    label="Stock (Box)"
                    name="stockCount"
                    type="number"
                    value={formData.stockCount}
                    onChange={handleChange}
                    error={!!errors.stockCount}
                    helperText={errors.stockCount}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Stock (Single)"
                    name="stockSingle"
                    type="number"
                    value={formData.stockSingle}
                    onChange={handleChange}
                    error={!!errors.stockSingle}
                    helperText={errors.stockSingle}
                    sx={{ mb: 2 }}
                  />
                </Box>
              )}
            </Box>

            {/* Right column */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Product Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                <Box>
                  <TextField
                    fullWidth
                    label="SKU"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    error={!!errors.sku}
                    helperText={errors.sku}
                  />
                </Box>
                <Box>
                  <FormControl fullWidth error={!!errors.categoryId}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleChange}
                      label="Category"
                    >
                      <MenuItem value={0}>Select a category</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.categoryId && (
                      <FormHelperText>{errors.categoryId}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <TextField
                    fullWidth
                    label="Product Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Unit Size"
                    name="unit_size"
                    type="text"
                    value={formData.unit_size}
                    onChange={handleChange}
                    error={!!errors.unit_size}
                    helperText={errors.unit_size}
                    placeholder="e.g., 500g, 1kg, 250ml, 5L, Size1"
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Quantity in a Box"
                    name="qtyInBox"
                    type="number"
                    value={formData.qtyInBox}
                    onChange={handleChange}
                    error={!!errors.qtyInBox}
                    helperText={errors.qtyInBox}
                  />
                </Box>
                <Box>
                  {isTypingNewUnit ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        fullWidth
                        label="New Unit Type"
                        value={customUnitType}
                        onChange={handleCustomUnitTypeChange}
                        error={!!errors.unitTypeId}
                        helperText={errors.unitTypeId}
                        placeholder="e.g., Pack, Pcs, Bottle"
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setIsTypingNewUnit(false);
                          setCustomUnitType('');
                          setFormData({
                            ...formData,
                            unitTypeId: '',
                          });
                        }}
                        sx={{ minWidth: 'auto', height: '56px' }}
                      >
                        ✕
                      </Button>
                    </Box>
                  ) : (
                    <FormControl fullWidth error={!!errors.unitTypeId}>
                      <InputLabel>Unit Type</InputLabel>
                      <Select
                        name="unitTypeId"
                        value={formData.unitTypeId}
                        onChange={handleUnitTypeChange}
                        label="Unit Type"
                      >
                        <MenuItem value="___TYPE_NEW_UNIT___" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                          + Type new unit
                        </MenuItem>
                        {unitTypes.map((unitType) => (
                          <MenuItem key={unitType} value={unitType}>
                            {unitType}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.unitTypeId && (
                        <FormHelperText>{errors.unitTypeId}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </Box>
                {!isFieldRestricted('cost_price', userRole) && (
                  <Box>
                    <TextField
                      fullWidth
                      label="Cost Price"
                      name="costPrice"
                      type="number"
                      inputProps={{ 
                        step: 0.01,
                        min: 0.00 // Allow values below 1
                      }}
                      onFocus={(e) => {
                        // Select all text when field is clicked
                        e.target.select();
                      }}
                      value={formData.costPrice}
                      onChange={handleChange}
                      error={!!errors.costPrice}
                      helperText={errors.costPrice}
                    />
                  </Box>
                )}
                {!isFieldRestricted('unit_price', userRole) && (
                  <Box>
                    <TextField
                      fullWidth
                      label="Unit Price"
                      name="unitPrice"
                      type="number"
                      inputProps={{ 
                        step: 0.01,
                        min: 0.00 // Allow values below 1
                      }}
                      onFocus={(e) => {
                        // Select all text when field is clicked
                        e.target.select();
                      }}
                      value={formData.unitPrice}
                      onChange={handleChange}
                      error={!!errors.unitPrice}
                      helperText={errors.unitPrice}
                    />
                  </Box>
                )}
                {!isFieldRestricted('tax_rate', userRole) && (
                  <Box>
                    <FormControl fullWidth error={!!errors.taxOptionId}>
                      <InputLabel>Tax</InputLabel>
                      <Select
                        name="taxOptionId"
                        value={formData.taxOptionId}
                        onChange={handleChange}
                        label="Tax"
                      >
                        {taxOptions.map((taxOption) => (
                          <MenuItem key={taxOption.id} value={taxOption.id}>
                            {taxOption.name} ({taxOption.rate}%)
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.taxOptionId && (
                        <FormHelperText>{errors.taxOptionId}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/products')}
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Product'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProductForm;
